import { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Clock,
  Shield,
  Loader2,
  ChevronRight,
  Send,
  X,
  FileText,
  Image,
  Mail,
  TrendingUp,
  Target,
  Zap,
  MessageCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/components/ui/utils';
import { coworkApi } from '../api/coworkApi';

// ─────────────────────────────────────────────
//  Types
// ─────────────────────────────────────────────

interface BoardPerspective {
  area: string;
  status: 'aligned' | 'concern' | 'blocker' | 'not_applicable';
  opinion: string;
  agentName?: string;
}

interface Approval {
  id: string;
  team_id: string;
  brand_id: string;
  type: string;
  title: string;
  requested_by: string;
  status: string;
  payload: string;
  user_response: string | null;
  created_at: string;
  resolved_at: string | null;
  board_perspectives?: string;
}

type FilterStatus = 'all' | 'pending' | 'approved' | 'rejected';

// ─────────────────────────────────────────────
//  Agent map
// ─────────────────────────────────────────────

const AGENT_MAP: Record<string, { name: string; emoji: string; color: string; avatarUrl?: string }> = {
  'account-manager': { name: 'Lykke', emoji: '🤝', color: '#2e024c' },
  'maya-seo':        { name: 'Maya',  emoji: '🔍', color: '#00b1a5' },
  'leo-perf':        { name: 'Leo',   emoji: '📊', color: '#ea580c' },
  'aria-content':    { name: 'Aria',  emoji: '✍️', color: '#6366f1' },
  'rex-influencer':  { name: 'Rex',   emoji: '👥', color: '#0ea5e9' },
  'nova-strategy':   { name: 'Nova',  emoji: '🧠', color: '#be43ff' },
  'kai-analytics':   { name: 'Kai',   emoji: '📈', color: '#059669' },
  'jones-artdir':    { name: 'Jones', emoji: '🎨', color: '#f59e0b' },
};

function agentInfo(id: string) {
  return AGENT_MAP[id] ?? { name: id, emoji: '🤖', color: '#6b7280' };
}

// ─────────────────────────────────────────────
//  Tool type → icon & label
// ─────────────────────────────────────────────

function toolInfo(type: string, title: string): { icon: React.ReactNode; label: string; color: string; bg: string } {
  if (type === 'heartbeat_action') {
    const toolName = title.split(': ')[1] || title;
    if (toolName.includes('propose_content')) return { icon: <MessageCircle size={14} />, label: 'Content', color: '#6366f1', bg: '#eef2ff' };
    if (toolName.includes('generate_document')) return { icon: <FileText size={14} />, label: 'Document', color: '#be43ff', bg: '#f5f0ff' };
    if (toolName.includes('generate_image')) return { icon: <Image size={14} />, label: 'Image', color: '#f59e0b', bg: '#fffbeb' };
    if (toolName.includes('gmail')) return { icon: <Mail size={14} />, label: 'Email', color: '#ea580c', bg: '#fff7ed' };
    if (toolName.includes('update_task') || toolName.includes('delete_task')) return { icon: <Target size={14} />, label: 'Task', color: '#0ea5e9', bg: '#f0f9ff' };
    if (toolName.includes('okr')) return { icon: <TrendingUp size={14} />, label: 'OKR', color: '#059669', bg: '#ecfdf5' };
    if (toolName.includes('gads') || toolName.includes('meta') || toolName.includes('tiktok')) return { icon: <Zap size={14} />, label: 'Ads', color: '#ff5635', bg: '#fff4f2' };
    return { icon: <Shield size={14} />, label: 'Action', color: '#6b7280', bg: '#f9fafb' };
  }
  // Legacy approval types
  const map: Record<string, { icon: React.ReactNode; label: string; color: string; bg: string }> = {
    budget_change: { icon: <Zap size={14} />, label: 'Budget', color: '#ff5635', bg: '#fff4f2' },
    okr_plan: { icon: <Target size={14} />, label: 'OKR Plan', color: '#059669', bg: '#ecfdf5' },
    new_initiative: { icon: <TrendingUp size={14} />, label: 'Initiative', color: '#be43ff', bg: '#f5f0ff' },
    content_publish: { icon: <MessageCircle size={14} />, label: 'Content', color: '#6366f1', bg: '#eef2ff' },
  };
  return map[type] ?? { icon: <Shield size={14} />, label: type.replace(/_/g, ' '), color: '#6b7280', bg: '#f9fafb' };
}

// ─────────────────────────────────────────────
//  Helpers
// ─────────────────────────────────────────────

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

function parsePayload(raw: string): Record<string, unknown> | null {
  try { const p = JSON.parse(raw); return typeof p === 'object' && p !== null ? p as Record<string, unknown> : null; }
  catch { return null; }
}

function parseBoardPerspectives(raw?: string): BoardPerspective[] {
  if (!raw) return [];
  try { const p = JSON.parse(raw); return Array.isArray(p) ? p : []; }
  catch { return []; }
}

// ─────────────────────────────────────────────
//  Render rich content (markdown-like)
// ─────────────────────────────────────────────

function renderRichContent(text: string) {
  const lines = text.split('\n');
  const elements: React.ReactElement[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.startsWith('## ')) {
      elements.push(<h2 key={i} className="text-[15px] font-bold text-gray-900 mt-4 mb-1.5">{line.slice(3)}</h2>);
    } else if (line.startsWith('### ')) {
      elements.push(<h3 key={i} className="text-[13px] font-semibold text-gray-800 mt-3 mb-1">{line.slice(4)}</h3>);
    } else if (line.startsWith('- ')) {
      const bullets: string[] = [];
      while (i < lines.length && lines[i].startsWith('- ')) { bullets.push(lines[i].slice(2)); i++; }
      elements.push(
        <ul key={`ul-${i}`} className="space-y-1 mb-2">
          {bullets.map((b, bi) => (
            <li key={bi} className="flex items-start gap-2 text-[13px] text-gray-700">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#be43ff] flex-shrink-0" />
              <span dangerouslySetInnerHTML={{ __html: b.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') }} />
            </li>
          ))}
        </ul>
      );
      continue;
    } else if (/^\d+\. /.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\. /.test(lines[i])) { items.push(lines[i].replace(/^\d+\. /, '')); i++; }
      elements.push(
        <ol key={`ol-${i}`} className="space-y-1 mb-2 list-decimal list-inside">
          {items.map((item, ii) => <li key={ii} className="text-[13px] text-gray-700" dangerouslySetInnerHTML={{ __html: item.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') }} />)}
        </ol>
      );
      continue;
    } else if (line.startsWith('| ')) {
      const rows: string[][] = [];
      while (i < lines.length && lines[i].startsWith('|')) {
        if (!lines[i].includes('---')) rows.push(lines[i].split('|').slice(1, -1).map(c => c.trim()));
        i++;
      }
      if (rows.length > 0) {
        elements.push(
          <div key={`table-${i}`} className="overflow-x-auto mb-3 rounded-lg border border-gray-100">
            <table className="w-full text-[12px] border-collapse">
              <thead><tr className="bg-gray-50">{rows[0].map((h, hi) => <th key={hi} className="text-left px-3 py-2 font-semibold text-gray-600">{h}</th>)}</tr></thead>
              <tbody>{rows.slice(1).map((row, ri) => <tr key={ri} className="border-t border-gray-50">{row.map((cell, ci) => <td key={ci} className="px-3 py-1.5 text-gray-700">{cell}</td>)}</tr>)}</tbody>
            </table>
          </div>
        );
      }
      continue;
    } else if (line.trim() === '') {
      elements.push(<div key={i} className="h-1.5" />);
    } else {
      elements.push(<p key={i} className="text-[13px] text-gray-700 leading-relaxed mb-1.5" dangerouslySetInnerHTML={{ __html: line.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>').replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" class="text-[#be43ff] underline hover:text-[#9333ea]">$1</a>') }} />);
    }
    i++;
  }
  return elements;
}

// ─────────────────────────────────────────────
//  Filters
// ─────────────────────────────────────────────

const FILTERS: { key: FilterStatus; label: string }[] = [
  { key: 'all',      label: 'All' },
  { key: 'pending',  label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
];

// ─────────────────────────────────────────────
//  Board advisors
// ─────────────────────────────────────────────

const BOARD_AGENTS: Record<string, { name: string; emoji: string; color: string; label: string }> = {
  strategy:   { name: 'Vera',   emoji: '🎯', color: '#be43ff', label: 'Strategy' },
  finance:    { name: 'Marcus', emoji: '💰', color: '#00b1a5', label: 'Finance' },
  legal:      { name: 'Elena',  emoji: '⚖️', color: '#ff5635', label: 'Legal' },
  operations: { name: 'Omar',   emoji: '⚙️', color: '#2e024c', label: 'Operations' },
};

const BOARD_STATUS: Record<string, { icon: string; color: string; bg: string }> = {
  aligned:        { icon: '✅', color: '#059669', bg: '#ecfdf5' },
  concern:        { icon: '⚠️', color: '#f59e0b', bg: '#fffbeb' },
  blocker:        { icon: '🔴', color: '#ef4444', bg: '#fef2f2' },
  not_applicable: { icon: '⬜', color: '#9ca3af', bg: '#f9fafb' },
};

// ─────────────────────────────────────────────
//  Detail Panel (slide-in from right)
// ─────────────────────────────────────────────

function ApprovalDetail({ approval, onClose, onResolve, resolving }: {
  approval: Approval;
  onClose: () => void;
  onResolve: (id: string, status: 'approved' | 'rejected', response?: string) => void;
  resolving: string | null;
}) {
  const [responseText, setResponseText] = useState('');
  const agent = agentInfo(approval.requested_by);
  const tool = toolInfo(approval.type, approval.title);
  const payload = parsePayload(approval.payload);
  const perspectives = parseBoardPerspectives(approval.board_perspectives);
  const isPending = approval.status === 'pending';
  const isResolving = resolving === approval.id;

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // Extract content for preview
  const toolInput = payload?.toolInput as Record<string, any> | undefined;
  const toolName = (payload?.toolName as string) || '';
  const contentBody = toolInput?.body || toolInput?.description || toolInput?.details || '';
  const contentTitle = toolInput?.title || '';

  return (
    <>
      <div className="fixed inset-0 bg-black/20 z-40" onClick={onClose} />
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-lg bg-white z-50 flex flex-col" style={{ boxShadow: '-8px 0 40px rgba(0,0,0,0.12)' }}>
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-5 border-b border-gray-100" style={{ background: `linear-gradient(135deg, ${agent.color}10, white)` }}>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: `${agent.color}15` }}>
                <span className="text-xl">{agent.emoji}</span>
              </div>
              <div>
                <p className="text-[13px] font-semibold" style={{ color: agent.color }}>{agent.name}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full" style={{ color: tool.color, background: tool.bg }}>
                    {tool.icon} {tool.label}
                  </span>
                  <span className="text-[11px] text-gray-400">{timeAgo(approval.created_at)}</span>
                </div>
              </div>
            </div>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
              <X size={16} />
            </button>
          </div>
          <h2 className="mt-4 text-[17px] font-bold text-gray-900 leading-snug">{approval.title}</h2>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Tool action summary */}
          {toolName && (
            <div className="rounded-xl bg-gray-50 border border-gray-100 p-4">
              <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Requested Action</p>
              <p className="text-[13px] font-medium text-gray-800">{toolName.replace(/_/g, ' ')}</p>
            </div>
          )}

          {/* Content preview */}
          {(contentTitle || contentBody) && (
            <div>
              {contentTitle && <h3 className="text-[15px] font-bold text-gray-900 mb-2">{contentTitle}</h3>}
              {contentBody && <div className="prose-sm">{renderRichContent(String(contentBody))}</div>}
            </div>
          )}

          {/* Full payload details */}
          {payload && (
            <div>
              <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Full Details</p>
              <div className="rounded-xl bg-gray-50 border border-gray-100 p-4 space-y-2">
                {Object.entries(toolInput || payload).map(([key, value]) => {
                  if (key === 'body' || key === 'description' || key === 'details') return null; // Already shown above
                  const strVal = typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value ?? '—');
                  return (
                    <div key={key}>
                      <span className="text-[11px] font-medium text-gray-500 capitalize">{key.replace(/_/g, ' ')}</span>
                      <p className="text-[13px] text-gray-700 whitespace-pre-wrap mt-0.5">{strVal}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Board perspectives */}
          {perspectives.length > 0 && (
            <div>
              <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Board Advisory</p>
              <div className="space-y-2">
                {perspectives.map((p) => {
                  const ba = BOARD_AGENTS[p.area];
                  const st = BOARD_STATUS[p.status] ?? BOARD_STATUS.not_applicable;
                  if (!ba) return null;
                  return (
                    <div key={p.area} className="flex items-start gap-3 rounded-xl p-3 border" style={{ background: st.bg, borderColor: `${st.color}20` }}>
                      <span className="text-sm mt-0.5">{st.icon}</span>
                      <div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-[12px] font-semibold" style={{ color: ba.color }}>{ba.name}</span>
                          <span className="text-[10px] text-gray-400">({ba.label})</span>
                        </div>
                        <p className="text-[12px] text-gray-600 leading-relaxed mt-0.5">{p.opinion}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Resolution status */}
          {!isPending && (
            <div className="rounded-xl border p-4" style={{ background: approval.status === 'approved' ? '#ecfdf5' : '#fef2f2', borderColor: approval.status === 'approved' ? '#05966920' : '#ef444420' }}>
              <div className="flex items-center gap-2 text-[13px] font-medium" style={{ color: approval.status === 'approved' ? '#059669' : '#ef4444' }}>
                {approval.status === 'approved' ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
                {approval.status === 'approved' ? 'Approved' : 'Rejected'}
                {approval.resolved_at && <span className="text-gray-400 font-normal ml-1">{timeAgo(approval.resolved_at)}</span>}
              </div>
              {approval.user_response && <p className="text-[12px] text-gray-600 mt-2">{approval.user_response}</p>}
            </div>
          )}
        </div>

        {/* Action bar (pending only) */}
        {isPending && (
          <div className="flex-shrink-0 border-t border-gray-100 px-6 py-4 bg-gray-50/50 space-y-3">
            <input
              type="text"
              value={responseText}
              onChange={(e) => setResponseText(e.target.value)}
              placeholder="Add a note (optional)..."
              className="w-full text-[13px] rounded-lg border border-gray-200 px-3 py-2 outline-none focus:border-[#be43ff] focus:ring-1 focus:ring-[#be43ff]/20 transition-all bg-white"
            />
            <div className="flex items-center gap-2">
              <button
                disabled={isResolving}
                onClick={() => onResolve(approval.id, 'rejected', responseText || undefined)}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-[13px] font-medium border border-red-200 text-red-500 bg-red-50 hover:bg-red-100 transition-all active:scale-[0.97] disabled:opacity-50"
              >
                <XCircle size={14} /> Decline
              </button>
              <div className="flex-1" />
              <button
                disabled={isResolving}
                onClick={() => onResolve(approval.id, 'approved', responseText || undefined)}
                className="flex items-center gap-1.5 px-6 py-2.5 rounded-lg text-[13px] font-semibold bg-[#a9f600] text-black hover:bg-[#95e000] transition-all active:scale-[0.97] shadow-sm disabled:opacity-50"
              >
                {isResolving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                Approve
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ─────────────────────────────────────────────
//  Stream Card (diary-style)
// ─────────────────────────────────────────────

function ApprovalCard({ approval, onClick }: { approval: Approval; onClick: () => void }) {
  const agent = agentInfo(approval.requested_by);
  const tool = toolInfo(approval.type, approval.title);
  const payload = parsePayload(approval.payload);
  const toolInput = payload?.toolInput as Record<string, any> | undefined;
  const preview = toolInput?.body || toolInput?.description || toolInput?.details || toolInput?.message || '';
  const isPending = approval.status === 'pending';

  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full text-left bg-white rounded-xl border p-4 transition-all group",
        isPending
          ? 'border-gray-100 hover:border-[#be43ff]/30 hover:shadow-sm'
          : 'border-gray-50 opacity-70 hover:opacity-90',
      )}
      style={{ boxShadow: isPending ? '0 1px 3px rgba(0,0,0,0.04)' : 'none' }}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: `${agent.color}12` }}>
          <span className="text-lg">{agent.emoji}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-[12px] font-semibold" style={{ color: agent.color }}>{agent.name}</span>
            <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full" style={{ color: tool.color, background: tool.bg }}>
              {tool.icon} {tool.label}
            </span>
            {isPending && (
              <span className="ml-auto flex h-2 w-2 rounded-full bg-orange-400 animate-pulse" />
            )}
            {!isPending && (
              <span className={cn("ml-auto text-[10px] font-medium px-1.5 py-0.5 rounded-full", approval.status === 'approved' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-500')}>
                {approval.status === 'approved' ? '✓ Approved' : '✗ Rejected'}
              </span>
            )}
            <span className="text-[11px] text-gray-400 flex-shrink-0">{timeAgo(approval.created_at)}</span>
          </div>
          <p className="text-[13px] font-semibold text-gray-900 leading-snug mb-1 group-hover:text-[#be43ff] transition-colors">
            {approval.title}
          </p>
          {preview && (
            <p className="text-[12px] text-gray-500 leading-relaxed line-clamp-2">
              {String(preview).slice(0, 200)}
            </p>
          )}
          <div className="mt-2 flex items-center gap-1 text-[11px] text-[#be43ff]">
            <ChevronRight size={12} />
            <span>View details</span>
          </div>
        </div>
      </div>
    </button>
  );
}

// ─────────────────────────────────────────────
//  Main page
// ─────────────────────────────────────────────

export function ApprovalsPage({ brandId, onBack }: { brandId?: string; onBack: () => void }) {
  const [filter, setFilter] = useState<FilterStatus>('pending');
  const [approvals, setApprovals] = useState<Approval[]>([]);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState<string | null>(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [selected, setSelected] = useState<Approval | null>(null);

  const fetchApprovals = useCallback(async (status: FilterStatus) => {
    setLoading(true);
    try {
      const apiStatus = status === 'all' ? undefined : status;
      const { approvals: data } = await coworkApi.approvals.list(brandId, apiStatus);
      setApprovals(data);
    } catch { setApprovals([]); }
    finally { setLoading(false); }
  }, [brandId]);

  const fetchPendingCount = useCallback(async () => {
    try { const { approvals: data } = await coworkApi.approvals.list(brandId, 'pending'); setPendingCount(data.length); }
    catch { /* silent */ }
  }, [brandId]);

  useEffect(() => { fetchApprovals(filter); fetchPendingCount(); }, [filter, fetchApprovals, fetchPendingCount]);

  const handleResolve = useCallback(async (id: string, status: 'approved' | 'rejected', response?: string) => {
    setResolving(id);
    try {
      await coworkApi.approvals.resolve(id, status, response);
      await fetchApprovals(filter);
      await fetchPendingCount();
      setSelected(null);
    } catch (err) {
      console.error('Failed to resolve:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to resolve approval');
    }
    finally { setResolving(null); }
  }, [filter, fetchApprovals, fetchPendingCount]);

  // Group by date
  const grouped = approvals.reduce<Record<string, Approval[]>>((acc, a) => {
    const date = a.created_at.split('T')[0];
    if (!acc[date]) acc[date] = [];
    acc[date].push(a);
    return acc;
  }, {});
  const sortedDates = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

  return (
    <div className="flex-1 flex flex-col min-w-0 relative bg-[#fafafa] rounded-tl-[24px] h-full">
      {/* Top bar */}
      <div className="h-12 shrink-0 flex items-center gap-3 px-5 border-b border-gray-100/60">
        <button onClick={onBack} className="text-gray-400 hover:text-gray-600 transition-colors">
          <ArrowLeft size={16} />
        </button>
        <div className="flex items-center gap-2">
          <Shield size={16} className="text-[#be43ff]" />
          <span className="text-[13px] font-semibold text-[#344054]">Approvals</span>
          {pendingCount > 0 && (
            <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-orange-500 px-1.5 text-[10px] font-bold text-white">
              {pendingCount}
            </span>
          )}
        </div>
        <div className="flex-1" />
        {/* Filter pills */}
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={cn(
                'px-2.5 py-1 rounded-md text-[11px] font-medium transition-all',
                filter === f.key ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700',
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Stream feed */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-6 py-6">
          {loading && (
            <div className="flex items-center justify-center py-20">
              <div className="w-6 h-6 border-2 border-[#be43ff] border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {!loading && approvals.length === 0 && (
            <div className="text-center py-20">
              <div className="w-14 h-14 rounded-full bg-[#ecfdf5] flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 size={28} className="text-[#059669]" />
              </div>
              <p className="text-[15px] font-medium text-gray-700 mb-1">
                {filter === 'pending' ? 'No pending approvals' : `No ${filter === 'all' ? '' : filter} approvals`}
              </p>
              <p className="text-[13px] text-gray-400 max-w-[320px] mx-auto">
                {filter === 'pending' ? 'Your agents are operating within approved parameters.' : 'Nothing to show for this filter.'}
              </p>
            </div>
          )}

          {!loading && sortedDates.map(date => (
            <div key={date} className="mb-8">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">{formatDate(date)}</span>
                <div className="flex-1 h-px bg-gray-100" />
                <span className="text-[11px] text-gray-400">{grouped[date].length}</span>
              </div>
              <div className="space-y-3">
                {grouped[date].map(a => (
                  <ApprovalCard key={a.id} approval={a} onClick={() => setSelected(a)} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Detail panel */}
      {selected && (
        <ApprovalDetail
          approval={selected}
          onClose={() => setSelected(null)}
          onResolve={handleResolve}
          resolving={resolving}
        />
      )}
    </div>
  );
}
